import {
  OrderJoinPreviewRideSeqItem,
  OrderJoinPreviewRideWaypoint,
  OrderJoinTransportingOrder,
} from "../../../common/services/order/join/order-join";
import OrderJoinError, {
  OrderJoinChangeTargetRideErrorType,
  OrderJoinErrorType,
} from "../../../common/services/order/join/order-join-error";
import { SearchRoadRoutingRequestWaypointCoordinate } from "../../../common/utils/search-road-route/search-road-routing.request";
import orderTranslationsHelper from "../../../languages/order-translations.helper";
import OrderJoinOrderDetailsConnectedWaypoints from "./common/types/order-join-order-details-connected-waypoints";
import OrderJoinOrderDetailsDriver from "./common/types/order-join-order-details-driver";
import OrderJoinOrderDetailsRouteWaypoint from "./common/types/order-join-order-details-route-waypoint";

const isOrderSolved = (
  responseOrderTransportingOrders: OrderJoinTransportingOrder[],
) => {
  if (responseOrderTransportingOrders.length === 0) {
    return false;
  }

  const isOrderSolved = responseOrderTransportingOrders.every(
    (item) => item.solvedOrder,
  );

  return isOrderSolved;
};

const isResponseRideSeqItemAWaypoint = (
  responseRideSeqItem: OrderJoinPreviewRideSeqItem,
): responseRideSeqItem is OrderJoinPreviewRideWaypoint => {
  if ("lat" in responseRideSeqItem) {
    return true;
  }

  return false;
};

const isRouteSequenceSolved = (
  waypoints: OrderJoinOrderDetailsRouteWaypoint[],
): boolean => {
  const waypointsStageNoArray = waypoints.map((waypoint) => waypoint.stageNo);

  return !waypointsStageNoArray.some((stageNo, index) => {
    return waypointsStageNoArray.indexOf(stageNo) != index;
  });
};

const getWaypointGroupsCouldBeConnected = (
  waypoints: OrderJoinOrderDetailsRouteWaypoint[],
) => {
  let waypointGroups: OrderJoinOrderDetailsConnectedWaypoints[] = [];

  waypoints.forEach((waypoint, index) => {
    const nextWaypoint = waypoints[index + 1];

    if (!nextWaypoint) {
      return;
    }

    const isWaypointAndNextWaypointSameStageNo =
      waypoint.stageNo === nextWaypoint.stageNo;
    const isWaypointAlone =
      waypoints.filter((w) => w.stageNo === waypoint.stageNo).length === 1;
    const isNextWaypointAlone =
      waypoints.filter((w) => w.stageNo === nextWaypoint.stageNo).length === 1;

    if (
      !isWaypointAndNextWaypointSameStageNo &&
      isWaypointAlone &&
      isNextWaypointAlone
    ) {
      waypointGroups.push({ start: waypoint, end: nextWaypoint });
    }
  });

  return waypointGroups;
};

const getCoordinatesOfWaypointsCouldBeConnected = (
  connectedWaypoints: OrderJoinOrderDetailsConnectedWaypoints[],
): SearchRoadRoutingRequestWaypointCoordinate[][] => {
  let coordinatesOfWaypointsCouldBeConnected: SearchRoadRoutingRequestWaypointCoordinate[][] =
    [];

  connectedWaypoints.forEach((connection) => {
    const start = {
      latitude: connection.start.place.latitude,
      longitude: connection.start.place.longitude,
    };

    const end = {
      latitude: connection.end.place.latitude,
      longitude: connection.end.place.longitude,
    };

    const newGroup: SearchRoadRoutingRequestWaypointCoordinate[] = [start, end];

    coordinatesOfWaypointsCouldBeConnected.push(newGroup);
  });

  return coordinatesOfWaypointsCouldBeConnected;
};

const getDriverButtonTitle = (driver: OrderJoinOrderDetailsDriver): string => {
  const translations = orderTranslationsHelper.getDetailsTranslations();

  return `${translations.drivers.driverButtonTitleTemplateText
    .replace("#{driverDisplayName}", driver.displayName)
    .replace("#{phoneNumber}", driver.phoneNumber)}`;
};

const getNotAcceptableErrorResponse = (error: OrderJoinError): string => {
  const translations = orderTranslationsHelper.getOrderJoinTranslations();

  switch (error.type) {
    case OrderJoinErrorType.CHANGE_RIDE_RELATED:
      switch (error.params.changeTargetRideError.errorType) {
        case OrderJoinChangeTargetRideErrorType.NODE_ALREADY_CHECKED_OUT:
          return translations.notAcceptableNodeCheckedOutErrorMessageText; // zlecenie rozpoczęte
        case OrderJoinChangeTargetRideErrorType.NO_VALID_CARGO_RAILY_CONTRACT:
        case OrderJoinChangeTargetRideErrorType.NO_VALID_CARGO_TAXI_CONTRACT:
          return translations.notAcceptableNoValidContractErrorMessageText; // brak aktywnego kontraktu
        case OrderJoinChangeTargetRideErrorType.NO_RELEVANT_DISPATCH_FOUND:
          return translations.notAcceptableNoRelevantDispatchErrorMessageText; // pasażerowie muszą być częścia obu firm cargo
        default:
          return translations.notAcceptableOtherErrorMessageText; // nie można połączyć zleceń
      }

    case OrderJoinErrorType.ORDER_ASSIGNED_TO_DRIVER:
      return translations.notAcceptableOrderAssignedToDriverErrorMessageText; // kierowca jest już obsadzony do zlecenia
  }
  return translations.notAcceptableOtherErrorMessageText;
};

const orderJoinHelper = {
  isOrderSolved,
  isRouteSequenceSolved,
  isResponseRideSeqItemAWaypoint,
  getWaypointGroupsCouldBeConnected,
  getCoordinatesOfWaypointsCouldBeConnected,
  getDriverButtonTitle,
  getNotAcceptableErrorResponse,
};

export default orderJoinHelper;
